import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AlloyButton, InlineLoader } from 'cat-ecommerce-alloy';
import cx from 'classnames';
import { PAGE_BREAKPOINTS, STATUS } from '@app/constants/commonConstants';
import { FBT_LIST_NAME } from '@app/constants/analyticsConstants';
import { useAddToCart, useAnalytics, useBreakpoint } from '@app/hooks';
import ContactDealerButton from '@app/components/pages/RepairOptions/common/ContactDealerButton';
import { checkIsRiyal, priceDisplayFormat } from '@app/utils/paymentUtils';
import styles from '../FrequentlyBoughtTogether.module.scss';
import { isRTLLang } from '@app/utils';
import { PCC_RELEASE_CURRENCY_SYMBOL_UPDATE_FOR_SAUDI_RIYAL } from '@app/constants/featureFlags';

const AddTheseToCart = ({ selectedItems }) => {
  const [t] = useTranslation();
  const { fireFbtAddToCartEvent } = useAnalytics();
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const current = useSelector(s => s.products?.current);
  const partNumber = useSelector(s => s.products.byId?.[current]?.partNumber);
  const fbtTotal = useSelector(s => s.products?.fbtTotal);
  const currency = useSelector(s => s.dealerCustomer?.currency);
  const quantity = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.quantity || 1
  );
  const fbtPriceAndAvailability = useSelector(
    s => s.products?.fbtPriceAndAvailability
  );
  const fbtItems = useSelector(s => s.products?.fbtItems?.products);
  const fbtItemsThree = fbtItems?.slice(0, 3);
  const finalItems = isLG ? fbtItems : fbtItemsThree;
  const unformattedTotalPrice = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.unformattedTotalPrice
  );
  const catEntryId = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.itemCatEntryId
  );
  const currentPartData = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]
  );
  const getFbtTotalStatus = useSelector(s => s.products?.getFbtTotalStatus);
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const sosCode = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.sosCode
  );
  const enableAddToCart = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.enableAddToCart
  );
  const currentPriceStatus = useSelector(
    s => s.products.getMarketingPriceStatus
  );
  const dealerStore = useSelector(s => s.common?.storeLocationName) ?? '';

  const addToCartItemCurrentItem = {
    productId: catEntryId,
    quantity: quantity.toString(),
    partNumber: partNumber,
    position: '1',
    price: unformattedTotalPrice
  };

  let addToCartItemFbtItems = [];
  finalItems?.map((item, index) => {
    const cardItem =
      fbtPriceAndAvailability !== undefined &&
      fbtPriceAndAvailability[item.partNumber];
    if (cardItem.enableAddToCart) {
      addToCartItemFbtItems.push({
        productId: cardItem.itemCatEntryId,
        quantity: '1',
        partNumber: cardItem.partNumber,
        position: index + 2,
        price: cardItem.unformattedTotalPrice
      });
    }
  });

  let productArray = [];
  finalItems?.map(item => {
    const dataItem =
      fbtPriceAndAvailability !== undefined &&
      fbtPriceAndAvailability[item.partNumber];
    productArray.push(dataItem);
  });
  const newProductData = productArray && [...productArray, currentPartData];
  const filteredProductData = newProductData?.filter(
    obj => obj?.enableAddToCart === true
  );
  const disableButton = selectedItems?.length === filteredProductData?.length;

  const allItemsToCart = () => {
    if (addToCartItemFbtItems?.length > 0) {
      if (enableAddToCart) {
        return [...addToCartItemFbtItems, addToCartItemCurrentItem];
      } else {
        return addToCartItemFbtItems;
      }
    } else {
      return [...addToCartItemFbtItems, addToCartItemCurrentItem];
    }
  };

  const allItemsData = allItemsToCart();
  let filterData = [];

  const { addToCart } = useAddToCart();
  const handleClick = () => {
    if (selectedItems.length > 0) {
      filterData = selectedItems
        .filter(item1 => {
          const item2 = allItemsData?.find(
            item2 => item2.partNumber === item1.id
          );
          return !item2;
        })
        .concat(
          allItemsData?.filter(item2 => {
            const item1 = selectedItems.find(
              item1 => item1.id === item2.partNumber
            );
            return !item1;
          })
        );
    }
    addToCart(filterData.length > 0 ? filterData : allItemsData, {
      x_calculateOrder: sosCode ?? ''
    });
    fireFbtAddToCartEvent(
      {
        products: filterData.length > 0 ? filterData : allItemsData,
        totalPrice: fbtTotal?.unformattedTotal,
        dealerStore
      },
      FBT_LIST_NAME
    );
  };

  const langId = useSelector(s => s.common?.langId);
  const isRTL = isRTLLang(langId);
  const currencySymbolFeatureFlag = useSelector(
    state =>
      state?.featureFlag?.[PCC_RELEASE_CURRENCY_SYMBOL_UPDATE_FOR_SAUDI_RIYAL]
  );
  const isRiyal = currencySymbolFeatureFlag && checkIsRiyal(currency);

  return (
    <div className={cx(styles['add-to-cart__button'])}>
      <div>
        {getFbtTotalStatus === STATUS.PENDING ||
        currentPriceStatus === STATUS.PENDING ? (
          <InlineLoader />
        ) : (
          !disableButton &&
          fbtTotal?.unformattedTotal > 0 && (
            <p className="u-fs--16 text-univers u-color--ExtraDarkGray mb-2">
              {t('TOTAL_PRICE')}:{' '}
              <span
                className={`d-md-inline d-block fw-bold ps-1 ${styles['subtotal__value']}`}
              >
                {isRiyal
                  ? priceDisplayFormat({
                      price: fbtTotal?.formattedTotal,
                      currency,
                      isRTL: isRTL,
                      priceWrapperClasses: 'px-1'
                    })
                  : priceDisplayFormat({
                      price: fbtTotal?.formattedTotal,
                      currency
                    })}
              </span>
            </p>
          )
        )}
      </div>
      {unformattedTotalPrice > 0 || fbtTotal?.unformattedTotal > 0 ? (
        <AlloyButton
          buttonType="secondary"
          className="w-100 w-lg-auto"
          onClick={handleClick}
          disabled={disableButton}
        >
          {t('ADD_THESE_TO_CART')}
        </AlloyButton>
      ) : (
        !isCatCorp && <ContactDealerButton className="mt-1 w-100 w-lg-auto" />
      )}
    </div>
  );
};

AddTheseToCart.propTypes = {
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      val: PropTypes.string
    })
  )
};

export default AddTheseToCart;
